import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { getMapImageUrl } from '../../utils/formatters';
import ZoneEditorModal from './ZoneEditorModal';
import './AdminMaps.css';

const SUGGESTION_MODES = ['competitive', 'wingman', 'duel'];
const SUGGESTION_MODE_LABELS = { competitive: '5v5', wingman: '2v2', duel: '1v1' };

// Category value -> i18n key (resolved via t() in each component; module constant
// can't call hooks). Pool names reuse the public maps.* group; community reuses news.*.
const CATEGORY_LABELS = {
  active: 'maps.poolActive',
  reserve: 'maps.poolReserve',
  community: 'news.catCommunity',
  classic: 'maps.poolClassic',
};

const GAME_MODES = ['competitive', 'wingman', 'duel'];

const GAME_MODE_LABELS = {
  competitive: '5v5',
  wingman: '2v2',
  duel: '1v1',
};

// Status value -> i18n key for the nav/mesh badges; unknown values are shown raw.
const ANALYSIS_STATUS_LABELS = {
  ready: 'admin.mapStatusReady',
  official: 'admin.mapStatusOfficial',
  failed: 'admin.mapStatusFailed',
  empty: 'admin.mapStatusEmpty',
  unsupported: 'admin.mapStatusUnsupported',
  missing: 'admin.mapStatusMissing',
  pending: 'admin.mapStatusPending',
  '': 'admin.mapStatusUnknown',
};

/**
 * Nav-/mesh-status for a map card, as the API's analysis code sees it.
 *
 * - nav (`nav_status`): needed for map control and automatic heatmap zones.
 * - mesh (`tri_status`): the visibility mesh behind the line-of-sight
 *   wallhack flags. Official maps use awpy's bundled mesh, so an empty
 *   `tri_status` only means "missing" on workshop maps (numeric tag).
 * @param {Object} map - Map row from /api/admin/maps.
 * @returns {{nav: string, navOk: boolean, mesh: string, meshOk: boolean}}
 */
function analysisStatus(map) {
  const nav = map.nav_status || '';
  const workshop = /^\d+$/.test(map.tag || '');
  const mesh = !workshop && !map.tri_status ? 'official' : (map.tri_status || (workshop ? 'missing' : ''));
  return {
    nav,
    navOk: nav === 'ready',
    mesh,
    meshOk: mesh === 'ready' || mesh === 'official',
  };
}

/**
 * Admin panel for managing the map pool: add/edit/deactivate/remove maps
 * and upload card + radar images. Changes propagate to bot (veto/map selection)
 * and stats-page matches automatically via the shared API.
 * @param {Object} props
 * @param {Object} props.perms - Admin permissions (unused here; gated at router).
 */
export default function AdminMaps({ perms }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [maps, setMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [filter, setFilter] = useState('');
  const [modeFilter, setModeFilter] = useState('all');
  const [onlyMissingRadar, setOnlyMissingRadar] = useState(false);
  const [onlyMissingAnalysis, setOnlyMissingAnalysis] = useState(false);

  const fetchMaps = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(`${API_URL}/api/admin/maps`, {
        headers: getAuthHeaders(),
      });
      if (!resp.ok) {
        setError(t('admin.mapsFetchFailed', { status: resp.status }));
        setLoading(false);
        return;
      }
      const data = await resp.json();
      const list = Array.isArray(data) ? data : (data.results || []);
      setMaps(list);
    } catch (e) {
      setError(t('admin.mapsNetworkError', { msg: e.message }));
    }
    setLoading(false);
  }, [getAuthHeaders, t]);

  useEffect(() => {
    fetchMaps();
  }, [fetchMaps]);

  const handleDelete = async (map) => {
    try {
      const resp = await fetch(`${API_URL}/api/admin/maps/${map.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (resp.ok) {
        setConfirmDelete(null);
        fetchMaps();
      } else {
        const body = await resp.json().catch(() => ({}));
        alert(body.error || t('maps.errorWithStatus', { status: resp.status }));
      }
    } catch (e) {
      alert(t('admin.mapsErrorColon', { msg: e.message }));
    }
  };

  const grouped = maps.reduce((acc, m) => {
    const key = m.category || 'community';
    if (!acc[key]) acc[key] = [];
    acc[key].push(m);
    return acc;
  }, {});

  const visibleMaps = (list) => {
    let out = list;
    if (modeFilter !== 'all') {
      out = out.filter((m) => (m.game_modes || []).includes(modeFilter));
    }
    if (onlyMissingRadar) {
      out = out.filter((m) => !m.radar_url);
    }
    if (onlyMissingAnalysis) {
      out = out.filter((m) => {
        const st = analysisStatus(m);
        return !st.navOk || !st.meshOk;
      });
    }
    if (filter) {
      const q = filter.toLowerCase();
      out = out.filter(
        (m) => m.name.toLowerCase().includes(q) || m.tag.toLowerCase().includes(q),
      );
    }
    return out;
  };

  const missingRadarCount = maps.filter((m) => !m.radar_url).length;
  const missingAnalysisCount = maps.filter((m) => {
    const st = analysisStatus(m);
    return !st.navOk || !st.meshOk;
  }).length;

  return (
    <div>
      <h1 className="admin-section-title">{t('nav.maps')}</h1>

      <PendingSuggestions onApproved={fetchMaps} />

      <div className="admin-maps-toolbar">
        <input
          className="admin-maps-search"
          type="text"
          placeholder={t('admin.mapsSearchPlaceholder')}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <div className="admin-maps-mode-filter">
          {[
            ['all', t('matches.all')],
            ['competitive', '5v5'],
            ['wingman', '2v2'],
            ['duel', '1v1'],
          ].map(([val, label]) => (
            <button
              key={val}
              type="button"
              className={`admin-maps-btn ${modeFilter === val ? 'primary' : ''}`}
              onClick={() => setModeFilter(val)}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className={`admin-maps-btn ${onlyMissingRadar ? 'warning' : ''}`}
          title={t('admin.mapsMissingRadarTitle', { count: missingRadarCount })}
          onClick={() => setOnlyMissingRadar((v) => !v)}
        >
          ⚠ {t('admin.mapsWithoutRadar')} ({missingRadarCount})
        </button>
        <button
          type="button"
          className={`admin-maps-btn ${onlyMissingAnalysis ? 'warning' : ''}`}
          title={t('admin.mapsMissingAnalysisTitle', { count: missingAnalysisCount })}
          onClick={() => setOnlyMissingAnalysis((v) => !v)}
        >
          ⚠ {t('admin.mapsWithoutAnalysis')} ({missingAnalysisCount})
        </button>
        <button
          type="button"
          className="admin-maps-btn admin-maps-btn-primary"
          onClick={() => setShowAdd(true)}
        >
          + {t('admin.mapsAdd')}
        </button>
      </div>

      {error && <div className="admin-maps-error">{error}</div>}

      {loading ? (
        <div className="admin-loading">{t('live.loading')}</div>
      ) : (
        <>
          {Object.keys(CATEGORY_LABELS).map((cat) => {
            const list = visibleMaps(grouped[cat] || []);
            if (list.length === 0) return null;
            return (
              <section key={cat} className="admin-maps-section">
                <h3 className="admin-maps-section-title">
                  {t(CATEGORY_LABELS[cat])}{' '}
                  <span className="admin-maps-count">({list.length})</span>
                </h3>
                <div className="admin-maps-grid">
                  {list.map((m) => (
                    <MapCard
                      key={m.id}
                      map={m}
                      onUpdated={fetchMaps}
                      onDelete={() => setConfirmDelete(m)}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </>
      )}

      {showAdd && (
        <AddMapModal
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            fetchMaps();
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          map={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => handleDelete(confirmDelete)}
        />
      )}
    </div>
  );
}

/**
 * Card for a single map with inline edit, toggle active, and image uploads.
 * @param {Object} props
 * @param {Object} props.map - Map data from API.
 * @param {Function} props.onUpdated - Callback after a successful mutation.
 * @param {Function} props.onDelete - Callback when user requests deletion.
 */
function MapCard({ map, onUpdated, onDelete }) {
  const { t } = useTranslation();
  const { getAuthHeaders, user } = useAuth();
  const imgInput = useRef(null);
  const radarInput = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(map.name);
  const [zoneEditorOpen, setZoneEditorOpen] = useState(false);

  useEffect(() => {
    setNameDraft(map.name);
  }, [map.name]);

  const patch = async (body) => {
    setBusy(true);
    setErr('');
    try {
      const resp = await fetch(`${API_URL}/api/admin/maps/${map.id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const b = await resp.json().catch(() => ({}));
        setErr(b.error || b.detail || t('maps.errorWithStatus', { status: resp.status }));
      } else {
        onUpdated();
      }
    } catch (e) {
      setErr(e.message);
    }
    setBusy(false);
  };

  const upload = async (file, kind) => {
    if (!file) return;
    setBusy(true);
    setErr('');
    try {
      const form = new FormData();
      form.append('file', file);
      const resp = await fetch(
        `${API_URL}/api/admin/maps/${map.id}/upload-${kind}`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: form,
        },
      );
      if (!resp.ok) {
        const b = await resp.json().catch(() => ({}));
        setErr(b.error || b.detail || t('maps.errorWithStatus', { status: resp.status }));
      } else {
        onUpdated();
      }
    } catch (e) {
      setErr(e.message);
    }
    setBusy(false);
  };

  const toggleMode = (mode) => {
    const current = map.game_modes || [];
    const next = current.includes(mode)
      ? current.filter((m) => m !== mode)
      : [...current, mode];
    if (next.length === 0) {
      setErr(t('admin.mapsModeRequired'));
      return;
    }
    patch({ game_modes: next });
  };

  // Cache-bust with updated_at so replaced images appear immediately.
  const cacheKey = map.updated_at ? `?v=${Date.parse(map.updated_at)}` : '';
  const imgUrl = map.image_url
    ? `${getMapImageUrl(map.image_url)}${cacheKey}`
    : null;

  return (
    <div className={`admin-maps-card ${!map.active ? 'inactive' : ''}`}>
      <div className="admin-map-thumb">
        {imgUrl ? (
          <img src={imgUrl} alt={map.name} />
        ) : (
          <div className="admin-map-placeholder">🗺️</div>
        )}
        {!map.active && <div className="admin-map-badge">{t('admin.mapInactive')}</div>}
        {!map.radar_url && (
          <div className="admin-map-badge admin-map-badge-warn" title={t('admin.mapRadarMissingTitle')}>
            ⚠ {t('admin.mapRadarMissing')}
          </div>
        )}
      </div>

      <div className="admin-map-body">
        {editingName ? (
          <form
            className="admin-map-name-edit"
            onSubmit={async (e) => {
              e.preventDefault();
              const trimmed = nameDraft.trim();
              if (!trimmed) {
                setErr(t('admin.mapNameRequired'));
                return;
              }
              if (trimmed === map.name) {
                setEditingName(false);
                return;
              }
              await patch({ name: trimmed });
              setEditingName(false);
            }}
          >
            <input
              type="text"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              disabled={busy}
              autoFocus
              maxLength={255}
            />
            <button type="submit" className="admin-maps-btn admin-maps-btn-primary" disabled={busy}>
              {t('settings.save')}
            </button>
            <button
              type="button"
              className="admin-maps-btn"
              onClick={() => { setNameDraft(map.name); setEditingName(false); setErr(''); }}
              disabled={busy}
            >
              {t('matchDetail.cancel')}
            </button>
          </form>
        ) : (
          <div className="admin-map-name admin-map-name-row">
            <span>{map.name}</span>
            <button
              type="button"
              className="admin-map-name-edit-btn"
              title={t('admin.mapEditName')}
              onClick={() => setEditingName(true)}
            >
              ✎
            </button>
          </div>
        )}
        <div className="admin-map-tag">{map.tag}</div>
        <MapAnalysisBadges map={map} />

        <div className="admin-map-modes">
          {GAME_MODES.map((mode) => {
            const on = (map.game_modes || []).includes(mode);
            return (
              <button
                key={mode}
                type="button"
                disabled={busy}
                className={`admin-map-mode ${on ? 'on' : ''}`}
                onClick={() => toggleMode(mode)}
                title={mode}
              >
                {GAME_MODE_LABELS[mode]}
              </button>
            );
          })}
        </div>

        <div className="admin-map-actions">
          {/^\d+$/.test(map.tag) && (
            <button
              type="button"
              disabled={busy}
              className="admin-maps-btn"
              title={t('admin.mapFetchWorkshopTitle')}
              onClick={async () => {
                setBusy(true);
                setErr('');
                try {
                  const resp = await fetch(
                    `${API_URL}/api/admin/maps/${map.id}/fetch-workshop-image`,
                    { method: 'POST', headers: getAuthHeaders() },
                  );
                  if (!resp.ok) {
                    const b = await resp.json().catch(() => ({}));
                    setErr(b.error || b.detail || t('maps.errorWithStatus', { status: resp.status }));
                  } else {
                    onUpdated();
                  }
                } catch (e) {
                  setErr(e.message);
                }
                setBusy(false);
              }}
            >
              {t('admin.mapFetchWorkshop')}
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            className="admin-maps-btn"
            onClick={() => imgInput.current?.click()}
          >
            {t('admin.mapChangeImage')}
          </button>
          <button
            type="button"
            disabled={busy}
            className="admin-maps-btn"
            onClick={() => radarInput.current?.click()}
          >
            {t('admin.mapChangeRadar')}
          </button>
          <button
            type="button"
            disabled={busy || !map.radar_url}
            className="admin-maps-btn"
            title={!map.radar_url ? t('admin.mapZonesNeedRadar') : t('admin.mapZonesTitle')}
            onClick={() => setZoneEditorOpen(true)}
          >
            {t('admin.mapZones')} ({(map.zones || []).length})
          </button>
          <button
            type="button"
            disabled={busy}
            className={`admin-maps-btn ${map.active ? 'warning' : 'primary'}`}
            onClick={() => patch({ active: !map.active })}
          >
            {map.active ? t('admin.mapDeactivate') : t('admin.mapActivate')}
          </button>
          <button
            type="button"
            disabled={busy}
            className="admin-maps-btn danger"
            onClick={onDelete}
          >
            {t('admin.remove')}
          </button>
        </div>

        {err && <div className="admin-maps-error">{err}</div>}

        <input
          ref={imgInput}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={(e) => upload(e.target.files?.[0], 'image')}
        />
        <input
          ref={radarInput}
          type="file"
          accept="image/png,image/jpeg"
          style={{ display: 'none' }}
          onChange={(e) => upload(e.target.files?.[0], 'radar')}
        />
      </div>

      {zoneEditorOpen && (
        <ZoneEditorModal
          map={map}
          samplePlayerSteamId={user?.steam_id || user?.steamid64 || null}
          onClose={() => setZoneEditorOpen(false)}
          onSaved={onUpdated}
        />
      )}
    </div>
  );
}

/**
 * Modal for creating a new map with optional initial image uploads.
 * @param {Object} props
 * @param {Function} props.onClose - Cancel callback.
 * @param {Function} props.onCreated - Callback after successful create (and any upload).
 */
function AddMapModal({ onClose, onCreated }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [tag, setTag] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('community');
  const [gameModes, setGameModes] = useState(['competitive']);
  const [imgFile, setImgFile] = useState(null);
  const [radarFile, setRadarFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const toggleMode = (mode) => {
    setGameModes((cur) =>
      cur.includes(mode) ? cur.filter((m) => m !== mode) : [...cur, mode],
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!tag.trim() || !name.trim()) {
      setErr(t('admin.mapsNameTagRequired'));
      return;
    }
    if (gameModes.length === 0) {
      setErr(t('admin.mapsModeRequired'));
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const resp = await fetch(`${API_URL}/api/admin/maps`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tag: tag.trim(),
          name: name.trim(),
          category,
          game_modes: gameModes,
        }),
      });
      if (!resp.ok) {
        const b = await resp.json().catch(() => ({}));
        setErr(b.error || b.detail || t('maps.errorWithStatus', { status: resp.status }));
        setBusy(false);
        return;
      }
      const created = await resp.json();
      // Upload images sequentially if provided
      const uploads = [];
      if (imgFile) uploads.push(['image', imgFile]);
      if (radarFile) uploads.push(['radar', radarFile]);
      for (const [kind, file] of uploads) {
        const form = new FormData();
        form.append('file', file);
        const up = await fetch(
          `${API_URL}/api/admin/maps/${created.id}/upload-${kind}`,
          {
            method: 'POST',
            headers: getAuthHeaders(),
            body: form,
          },
        );
        if (!up.ok) {
          const b = await up.json().catch(() => ({}));
          setErr(t('admin.mapUploadFailed', { kind, error: b.error || up.status }));
          setBusy(false);
          onCreated();
          return;
        }
      }
      onCreated();
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="admin-maps-modal-overlay" onClick={onClose}>
      <div className="admin-maps-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{t('admin.mapsAdd')}</h3>
        <form onSubmit={submit}>
          <label className="admin-maps-field">
            <span>{t('admin.mapTagLabel')}</span>
            <input
              type="text"
              placeholder={t('admin.mapTagPlaceholder')}
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              required
            />
          </label>
          <label className="admin-maps-field">
            <span>{t('admin.mapDisplayName')}</span>
            <input
              type="text"
              placeholder={t('admin.mapNamePlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </label>
          <label className="admin-maps-field">
            <span>{t('admin.category')}</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{t(v)}</option>
              ))}
            </select>
          </label>
          <div className="admin-maps-field">
            <span>{t('admin.mapGameModes')}</span>
            <div className="admin-map-modes">
              {GAME_MODES.map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={`admin-map-mode ${gameModes.includes(mode) ? 'on' : ''}`}
                  onClick={() => toggleMode(mode)}
                >
                  {GAME_MODE_LABELS[mode]}
                </button>
              ))}
            </div>
          </div>
          <label className="admin-maps-field">
            <span>{t('admin.mapImageLabel')}</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => setImgFile(e.target.files?.[0] || null)}
            />
          </label>
          <label className="admin-maps-field">
            <span>{t('admin.mapRadarLabel')}</span>
            <input
              type="file"
              accept="image/png,image/jpeg"
              onChange={(e) => setRadarFile(e.target.files?.[0] || null)}
            />
          </label>

          {err && <div className="admin-maps-error">{err}</div>}

          <div className="admin-maps-modal-actions">
            <button type="button" className="admin-maps-btn" onClick={onClose} disabled={busy}>
              {t('matchDetail.cancel')}
            </button>
            <button type="submit" className="admin-maps-btn admin-maps-btn-primary" disabled={busy}>
              {busy ? t('admin.creating') : t('admin.mapCreate')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Confirmation dialog for deleting a map.
 * @param {Object} props
 * @param {Object} props.map - Map to delete.
 * @param {Function} props.onCancel - Cancel callback.
 * @param {Function} props.onConfirm - Confirm callback.
 */
function ConfirmDeleteModal({ map, onCancel, onConfirm }) {
  const { t } = useTranslation();
  return (
    <div className="admin-maps-modal-overlay" onClick={onCancel}>
      <div className="admin-maps-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{t('admin.mapDeleteConfirm', { name: map.name })}</h3>
        <p>{t('admin.mapDeleteWarning')}</p>
        <div className="admin-maps-modal-actions">
          <button type="button" className="admin-maps-btn" onClick={onCancel}>
            {t('matchDetail.cancel')}
          </button>
          <button type="button" className="admin-maps-btn danger" onClick={onConfirm}>
            {t('admin.mapDelete')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Pending community map suggestions — reviewed inline with approve/reject.
 * Hidden when the list is empty so the admin page stays tidy.
 * @param {Object} props
 * @param {Function} props.onApproved - Called after approve so the main
 *   map list refetches and the new map shows up immediately.
 */
function PendingSuggestions({ onApproved }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchPending = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(`${API_URL}/api/admin/maps/suggestions`, {
        headers: getAuthHeaders(),
      });
      if (!resp.ok) {
        setError(t('admin.mapsSuggestFetchFailed', { status: resp.status }));
        setLoading(false);
        return;
      }
      const data = await resp.json();
      setItems(Array.isArray(data) ? data : (data.results || []));
    } catch (e) {
      setError(t('admin.mapsNetworkError', { msg: e.message }));
    }
    setLoading(false);
  }, [getAuthHeaders, t]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  if (loading) return null;
  if (error) return <div className="admin-maps-error">{error}</div>;
  if (items.length === 0) return null;

  return (
    <section className="admin-maps-section admin-suggestions">
      <h3 className="admin-maps-section-title">
        {t('admin.mapsPendingSuggestions')} <span className="admin-maps-count">({items.length})</span>
      </h3>
      <div className="admin-suggestions-list">
        {items.map((s) => (
          <SuggestionRow
            key={s.id}
            suggestion={s}
            onResolved={() => {
              fetchPending();
              onApproved?.();
            }}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * Single pending suggestion with approve/reject controls. Approve lets
 * the admin choose category + game modes before promoting to a real Map;
 * reject asks for an optional reason.
 * @param {Object} props
 * @param {Object} props.suggestion - Suggestion data from the API.
 * @param {Function} props.onResolved - Called after approve or reject.
 */
function SuggestionRow({ suggestion, onResolved }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [category, setCategory] = useState('community');
  const [gameModes, setGameModes] = useState(['competitive']);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [name, setName] = useState(suggestion.name || suggestion.tag);

  const toggleMode = (mode) => {
    setGameModes((cur) =>
      cur.includes(mode) ? cur.filter((m) => m !== mode) : [...cur, mode],
    );
  };

  const approve = async () => {
    if (gameModes.length === 0) {
      setErr(t('admin.mapsModeRequired'));
      return;
    }
    if (!name.trim()) {
      setErr(t('admin.mapSuggestNameRequired'));
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const resp = await fetch(
        `${API_URL}/api/admin/maps/suggestions/${suggestion.id}/approve`,
        {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ category, game_modes: gameModes, name: name.trim() }),
        },
      );
      if (!resp.ok) {
        const b = await resp.json().catch(() => ({}));
        setErr(b.error || b.detail || t('maps.errorWithStatus', { status: resp.status }));
        setBusy(false);
        return;
      }
      onResolved();
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    setErr('');
    try {
      const resp = await fetch(
        `${API_URL}/api/admin/maps/suggestions/${suggestion.id}/reject`,
        {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: rejectReason }),
        },
      );
      if (!resp.ok) {
        const b = await resp.json().catch(() => ({}));
        setErr(b.error || b.detail || t('maps.errorWithStatus', { status: resp.status }));
        setBusy(false);
        return;
      }
      onResolved();
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  };

  const numeric = /^\d+$/.test(suggestion.tag);

  return (
    <div className="admin-suggestion-card">
      <div className="admin-suggestion-thumb">
        {suggestion.preview_image_url ? (
          <img src={suggestion.preview_image_url} alt={suggestion.name} />
        ) : (
          <div className="admin-map-placeholder">🗺️</div>
        )}
      </div>
      <div className="admin-suggestion-body">
        <div className="admin-suggestion-header">
          <div>
            <div className="admin-map-name">{suggestion.name || suggestion.tag}</div>
            <div className="admin-map-tag">
              {suggestion.tag}
              {numeric && (
                <a
                  href={`https://steamcommunity.com/sharedfiles/filedetails/?id=${suggestion.tag}`}
                  target="_blank"
                  rel="noreferrer"
                  className="admin-suggestion-link"
                >
                  {t('admin.mapOpenWorkshop')}
                </a>
              )}
            </div>
          </div>
          <div className="admin-suggestion-meta">
            {t('admin.mapSuggestedBy')} <strong>{suggestion.submitted_by_username || '?'}</strong>
            <span className="admin-suggestion-date">
              {suggestion.created_at
                ? new Date(suggestion.created_at).toLocaleDateString('sv-SE')
                : ''}
            </span>
          </div>
        </div>

        {suggestion.notes && (
          <blockquote className="admin-suggestion-notes">{suggestion.notes}</blockquote>
        )}

        {!showReject && (
          <>
            <div className="admin-suggestion-controls">
              <label>
                <span>{t('admin.mapDisplayName')}</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={busy}
                  maxLength={255}
                  placeholder={t('admin.mapNameUiPlaceholder')}
                />
              </label>
              <label>
                <span>{t('admin.category')}</span>
                <select value={category} onChange={(e) => setCategory(e.target.value)} disabled={busy}>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{t(v)}</option>
                  ))}
                </select>
              </label>
              <div>
                <span>{t('admin.mapGameModes')}</span>
                <div className="admin-map-modes">
                  {SUGGESTION_MODES.map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      disabled={busy}
                      className={`admin-map-mode ${gameModes.includes(mode) ? 'on' : ''}`}
                      onClick={() => toggleMode(mode)}
                    >
                      {SUGGESTION_MODE_LABELS[mode]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {err && <div className="admin-maps-error">{err}</div>}
            <div className="admin-suggestion-actions">
              <button
                type="button"
                disabled={busy}
                className="admin-maps-btn"
                onClick={() => setShowReject(true)}
              >
                {t('admin.mapReject')}
              </button>
              <button
                type="button"
                disabled={busy}
                className="admin-maps-btn admin-maps-btn-primary"
                onClick={approve}
              >
                {busy ? t('admin.mapApproving') : t('admin.mapApprove')}
              </button>
            </div>
          </>
        )}

        {showReject && (
          <>
            <label className="admin-maps-field">
              <span>{t('admin.mapRejectReason')}</span>
              <input
                type="text"
                placeholder={t('admin.mapRejectReasonPlaceholder')}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                maxLength={255}
              />
            </label>
            {err && <div className="admin-maps-error">{err}</div>}
            <div className="admin-suggestion-actions">
              <button
                type="button"
                disabled={busy}
                className="admin-maps-btn"
                onClick={() => setShowReject(false)}
              >
                {t('profile.back')}
              </button>
              <button
                type="button"
                disabled={busy}
                className="admin-maps-btn danger"
                onClick={reject}
              >
                {busy ? t('admin.mapRejecting') : t('admin.mapConfirmReject')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Two small badges on a map card: nav and visibility-mesh status.
 * Green = ready (or official mesh), red = missing/broken. The title text
 * says what each one powers so an admin knows what a red badge costs.
 * @param {Object} props
 * @param {Object} props.map - Map row from /api/admin/maps.
 */
function MapAnalysisBadges({ map }) {
  const { t } = useTranslation();
  const st = analysisStatus(map);
  const label = (value) => (ANALYSIS_STATUS_LABELS[value] !== undefined ? t(ANALYSIS_STATUS_LABELS[value]) : value);
  return (
    <div className="admin-map-analysis">
      <span
        className={`admin-map-analysis-badge ${st.navOk ? 'ok' : 'bad'}`}
        title={t('admin.mapNavTitle')}
      >
        {t('admin.mapNavLabel')}: {label(st.nav)}
      </span>
      <span
        className={`admin-map-analysis-badge ${st.meshOk ? 'ok' : 'bad'}`}
        title={t('admin.mapMeshTitle')}
      >
        {t('admin.mapMeshLabel')}: {label(st.mesh)}
      </span>
    </div>
  );
}
